import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Checkbox } from "../../components/ui/checkbox";
import { Plus, Edit2, Trash2, Package, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBundles() {
  const { getAuthHeader } = useAuth();
  const [bundles, setBundles] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    package_ids: [],
    price: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bundlesRes, packagesRes] = await Promise.all([
        axios.get(`${API}/admin/bundles`, getAuthHeader()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/packages`, getAuthHeader())
      ]);
      setBundles(bundlesRes.data || []);
      setPackages(packagesRes.data.filter(p => p.is_active));
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const calculateOriginalPrice = () => {
    return formData.package_ids.reduce((total, pkgId) => {
      const pkg = packages.find(p => p.id === pkgId);
      return total + (pkg?.price || 0);
    }, 0);
  };

  const calculateDiscount = () => {
    const original = calculateOriginalPrice();
    const price = parseFloat(formData.price) || 0;
    if (original === 0) return 0;
    return ((original - price) / original * 100).toFixed(1);
  };

  const handlePackageToggle = (pkgId) => {
    setFormData(prev => {
      const newIds = prev.package_ids.includes(pkgId)
        ? prev.package_ids.filter(id => id !== pkgId)
        : [...prev.package_ids, pkgId];
      return { ...prev, package_ids: newIds };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.package_ids.length < 2) {
      toast.error("En az 2 paket seçmelisiniz");
      return;
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error("Geçerli bir fiyat girin");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        package_ids: formData.package_ids,
        price: parseFloat(formData.price)
      };

      if (editingBundle) {
        await axios.put(`${API}/admin/bundles/${editingBundle.id}`, payload, getAuthHeader());
        toast.success("Kombo güncellendi");
      } else {
        await axios.post(`${API}/admin/bundles`, payload, getAuthHeader());
        toast.success("Kombo oluşturuldu");
      }
      
      fetchData();
      closeDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bundleId) => {
    if (!window.confirm("Bu komboyu silmek istediğinize emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/admin/bundles/${bundleId}`, getAuthHeader());
      toast.success("Kombo silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const openCreateDialog = () => {
    setEditingBundle(null);
    setFormData({
      name: "",
      description: "",
      package_ids: [],
      price: ""
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (bundle) => {
    setEditingBundle(bundle);
    setFormData({
      name: bundle.name,
      description: bundle.description,
      package_ids: bundle.package_ids || [],
      price: bundle.price.toString()
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingBundle(null);
    setFormData({
      name: "",
      description: "",
      package_ids: [],
      price: ""
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Kombo Paketler">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-red-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Kombo Paketler">
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Nunito']">Kombo Paketler</h1>
          <p className="text-neutral-400 text-sm mt-1">
            Birden fazla paketi birleştirerek indirimli kombo paketler oluşturun
          </p>
        </div>
        <Button 
          onClick={openCreateDialog}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="create-bundle-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Kombo
        </Button>
      </div>

      {/* Bundle List */}
      <div className="bg-neutral-900/50 rounded-xl border border-neutral-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-neutral-800 hover:bg-transparent">
              <TableHead className="text-neutral-400">Kombo Adı</TableHead>
              <TableHead className="text-neutral-400">İçerik</TableHead>
              <TableHead className="text-neutral-400">Normal Fiyat</TableHead>
              <TableHead className="text-neutral-400">Kombo Fiyat</TableHead>
              <TableHead className="text-neutral-400">İndirim</TableHead>
              <TableHead className="text-neutral-400 text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bundles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-neutral-500 py-8">
                  Henüz kombo paket oluşturulmamış
                </TableCell>
              </TableRow>
            ) : (
              bundles.map((bundle) => (
                <TableRow key={bundle.id} className="border-neutral-800">
                  <TableCell className="text-white font-medium">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-amber-500" />
                      {bundle.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-neutral-300">
                    <div className="flex flex-wrap gap-1">
                      {bundle.packages?.map(pkg => (
                        <span key={pkg.id} className="text-xs bg-neutral-800 px-2 py-1 rounded">
                          {pkg.name}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-neutral-400 line-through">
                    {bundle.original_price} €
                  </TableCell>
                  <TableCell className="text-amber-400 font-semibold">
                    {bundle.price} €
                  </TableCell>
                  <TableCell>
                    <span className="bg-green-600/20 text-green-400 px-2 py-1 rounded text-sm">
                      %{bundle.discount_percentage}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditDialog(bundle)}
                        className="text-neutral-400 hover:text-white"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(bundle.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg bg-neutral-900 border-neutral-800">
          <DialogHeader>
            <DialogTitle className="text-white font-['Nunito']">
              {editingBundle ? "Kombo Düzenle" : "Yeni Kombo Oluştur"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-neutral-300">Kombo Adı</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Örn: A1+A2 Başlangıç Paketi"
                className="bg-neutral-800 border-neutral-700 text-white"
                required
                data-testid="bundle-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-neutral-300">Açıklama</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Kombo paket açıklaması..."
                className="bg-neutral-800 border-neutral-700 text-white"
                rows={2}
                data-testid="bundle-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-neutral-300">Paketleri Seçin (en az 2)</Label>
              <div className="bg-neutral-800 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                {packages.map((pkg) => (
                  <label 
                    key={pkg.id} 
                    className="flex items-center gap-3 p-2 rounded hover:bg-neutral-700/50 cursor-pointer"
                  >
                    <Checkbox
                      checked={formData.package_ids.includes(pkg.id)}
                      onCheckedChange={() => handlePackageToggle(pkg.id)}
                      className="border-neutral-600"
                    />
                    <span className="text-white flex-1">{pkg.name}</span>
                    <span className="text-neutral-400 text-sm">€{pkg.price}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-neutral-500">
                Seçili: {formData.package_ids.length} paket
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-neutral-300">Kombo Fiyatı (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="İndirimli fiyat"
                className="bg-neutral-800 border-neutral-700 text-white"
                required
                data-testid="bundle-price-input"
              />
            </div>

            {/* Price Summary */}
            {formData.package_ids.length >= 2 && formData.price && (
              <div className="bg-neutral-800/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-400">Normal Fiyat:</span>
                  <span className="text-neutral-300 line-through">€{calculateOriginalPrice()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-400">Kombo Fiyat:</span>
                  <span className="text-amber-400 font-semibold">€{formData.price}</span>
                </div>
                <div className="flex justify-between text-sm border-t border-neutral-700 pt-2">
                  <span className="text-neutral-400">İndirim:</span>
                  <span className="text-green-400 font-semibold">%{calculateDiscount()}</span>
                </div>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-amber-600 hover:bg-amber-700" 
              disabled={saving}
              data-testid="save-bundle-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingBundle ? "Güncelle" : "Oluştur"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    </AdminLayout>
  );
}
